import styles from "./styles.module.scss";
import { Spin } from "antd";
import {
  LineChart,
  Line,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { useEffect, useState } from "react";
import { ProductsService } from "@services/amazon/products/products.service";
import {
  getViewOptions,
  getDateRangeByView,
  formatReviewScoreChartData,
} from "./utils";
import { ChartOverviewProps } from "./types";

export const ChartOverview = ({ asin }: ChartOverviewProps) => {
  const [view, setView] = useState("week");
  const [chartData, setChartData] = useState([]);
  const productsService = new ProductsService();
  const [isLoading, setIsLoading] = useState(true);

  const handleViewChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setView(e.target.value);
  };

  useEffect(() => {
    if (!asin) return;

    const { date_start, date_end } = getDateRangeByView(view);

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await productsService.getReviewScore({
          asin,
          date_start,
          date_end,
          sort_direction: "ASC",
          group_by: "DAY",
        });

        const formattedData = formatReviewScoreChartData(response ?? []);
        setChartData(formattedData);
      } catch (error) {
        console.error("Failed to fetch review score:", error);
        setChartData([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [view, asin]);

  return (
    <div className={styles.chartWrapper}>
      <div className={styles.chartHeader}>
        <h2>Review Score</h2>
        <select
          className={styles.selectDropdown}
          value={view}
          onChange={handleViewChange}
        >
          {getViewOptions().map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
      <div style={{ width: "100%", height: "320px" }}>
        {isLoading ? (
          <div className="loading">
            <Spin />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="0" />
              <YAxis
                yAxisId="left"
                domain={[0, 5]}
                tick={{ fill: "#666", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={[20, 60]}
                tick={{ fill: "#666", fontSize: 12 }}
                tickFormatter={(value) => `${value}%`}
                axisLine={false}
                tickLine={false}
              />
              <Line
                yAxisId="left"
                type="monotone"
                dataKey="reviewScore"
                stroke="#7F5AF0"
                strokeWidth={2}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};
